import {
  Children,
  createElement,
  isValidElement,
  type CSSProperties,
  type ElementType,
  type ReactNode,
} from 'react'
import { useInView } from '@/hooks/useInView'
import { cn } from '@/lib/cn'

/** Bold-style easing: a smooth, slightly-overshooting ease-out. */
const EASE = 'cubic-bezier(0.22, 1, 0.36, 1)'

export type RevealVariant = 'fade' | 'up' | 'down' | 'left' | 'right' | 'zoom'

/** The transform applied while the element is still hidden. */
const OFFSET: Record<RevealVariant, string> = {
  fade: 'translate3d(0, 0, 0)',
  up: 'translate3d(0, 48px, 0)',
  down: 'translate3d(0, -48px, 0)',
  left: 'translate3d(48px, 0, 0)',
  right: 'translate3d(-48px, 0, 0)',
  zoom: 'scale(0.9)',
}

export interface RevealProps {
  children: ReactNode
  /** Element/tag to render. Defaults to a div. */
  as?: ElementType
  /** Direction the content animates in from. */
  variant?: RevealVariant
  /** Delay before animating, in ms (handy for staggering). */
  delay?: number
  /** Animation duration in ms. */
  duration?: number
  /** Animate only the first time it enters the viewport. */
  once?: boolean
  className?: string
  style?: CSSProperties
}

/**
 * Animates its children into view as the user scrolls; the core
 * scroll-reveal effect used throughout the site (matches the Bold template).
 */
export const Reveal = ({
  children,
  as = 'div',
  variant = 'up',
  delay = 0,
  duration = 700,
  once = true,
  className,
  style,
}: RevealProps) => {
  const { ref, inView } = useInView({ once })

  return createElement(
    as,
    {
      ref,
      className: cn(className),
      style: {
        ...style,
        opacity: inView ? 1 : 0,
        transform: inView
          ? variant === 'zoom'
            ? 'scale(1)'
            : 'translate3d(0, 0, 0)'
          : OFFSET[variant],
        transition: `opacity ${duration}ms ${EASE} ${delay}ms, transform ${duration}ms ${EASE} ${delay}ms`,
        willChange: inView ? 'auto' : 'opacity, transform',
      } satisfies CSSProperties,
    },
    children,
  )
}

export interface RevealGroupProps {
  children: ReactNode
  /** Delay added between each child, in ms. */
  step?: number
  /** Delay before the first child animates, in ms. */
  delay?: number
  variant?: RevealVariant
  duration?: number
  once?: boolean
  /** Tag/element for the wrapping container (e.g. a grid or flex row). */
  as?: ElementType
  className?: string
}

/**
 * Staggers its direct children into view one after another.
 * Wrap a grid or list of cards to get the cascading reveal seen on Bold.
 */
export const RevealGroup = ({
  children,
  step = 90,
  delay = 0,
  variant = 'up',
  duration,
  once = true,
  as = 'div',
  className,
}: RevealGroupProps) => {
  const items = Children.toArray(children).filter(isValidElement)

  return createElement(
    as,
    { className: cn(className) },
    items.map((child, index) => (
      <Reveal
        key={child.key ?? index}
        variant={variant}
        duration={duration}
        delay={delay + index * step}
        once={once}
      >
        {child}
      </Reveal>
    )),
  )
}
